import { useState } from 'react';
import {
  VStack, Text, Heading, Button, Wrap, WrapItem,
} from '@chakra-ui/react';
import { WizardChip } from '../WizardChip';

const GENRES = [
  'Pop', 'Rock', 'Jazz', 'Classical', 'Electronic', 'Hip-Hop', 'R&B',
  'Country', 'Latin', 'Indie', 'Folk', 'Blues', 'Reggae', 'World',
  'Ambient', 'Lounge', 'Soul', 'Funk',
];

const MOODS = [
  'Upbeat', 'Relaxed', 'Energetic', 'Chill', 'Romantic', 'Motivating',
  'Peaceful', 'Happy', 'Melancholic', 'Groovy', 'Sophisticated', 'Cozy',
];

const TEMPOS = [
  { id: 'slow', label: '🐢 Slow' },
  { id: 'medium', label: '🚶 Medium' },
  { id: 'fast', label: '🏃 Fast' },
  { id: 'mixed', label: '🔀 Mixed' },
];

export interface MusicStepData {
  genres: string[];
  moods: string[];
  tempo: string;
}

export interface MusicStepProps {
  onSubmit: (data: { genres: string[]; moods: string[]; tempo: string }) => void;
  initialData?: { genres?: string[]; moods?: string[]; tempo?: string };
  isLoading?: boolean;
  error?: string | null;
  availableGenres?: { value: string; label: string; icon?: string }[];
  availableMoods?: { value: string; label: string; icon?: string }[];
}

export function MusicStep({ onSubmit, initialData, isLoading }: MusicStepProps) {
  const [selectedGenres, setSelectedGenres] = useState<string[]>(initialData?.genres ?? []);
  const [selectedMoods, setSelectedMoods] = useState<string[]>(initialData?.moods ?? []);
  const [selectedTempo, setSelectedTempo] = useState<string>(initialData?.tempo ?? '');

  const toggleGenre = (genre: string) => {
    setSelectedGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre],
    );
  };

  const toggleMood = (mood: string) => {
    setSelectedMoods((prev) =>
      prev.includes(mood) ? prev.filter((m) => m !== mood) : [...prev, mood],
    );
  };

  const canSubmit = selectedGenres.length > 0 && selectedMoods.length > 0 && selectedTempo;

  return (
    <VStack
      flex={1}
      p={6}
      gap={6}
      bg="gray.50"
      overflowY="auto"
      data-testid="wizard-music-step"
    >
      <VStack align="center" gap={2}>
        <Heading as="h2" size="xl" fontWeight="bold" color="gray.800">
          Music preferences
        </Heading>
        <Text fontSize="md" color="gray.500" textAlign="center">
          Select the genres, moods, and tempo for your location
        </Text>
      </VStack>

      {/* Genres */}
      <VStack gap={3} w="100%" maxW="600px" alignSelf="center" align="stretch">
        <Text fontSize="md" fontWeight="600" color="gray.800">
          Genres ({selectedGenres.length} selected)
        </Text>
        <Wrap spacing={2}>
          {GENRES.map((genre) => (
            <WrapItem key={genre}>
              <WizardChip
                label={genre}
                selected={selectedGenres.includes(genre)}
                onPress={() => toggleGenre(genre)}
                data-testid={`wizard-genre-${genre.toLowerCase()}`}
              />
            </WrapItem>
          ))}
        </Wrap>
      </VStack>

      {/* Moods */}
      <VStack gap={3} w="100%" maxW="600px" alignSelf="center" align="stretch">
        <Text fontSize="md" fontWeight="600" color="gray.800">
          Moods ({selectedMoods.length} selected)
        </Text>
        <Wrap spacing={2}>
          {MOODS.map((mood) => (
            <WrapItem key={mood}>
              <WizardChip
                label={mood}
                selected={selectedMoods.includes(mood)}
                onPress={() => toggleMood(mood)}
                data-testid={`wizard-mood-${mood.toLowerCase()}`}
              />
            </WrapItem>
          ))}
        </Wrap>
      </VStack>

      {/* Tempo */}
      <VStack gap={3} w="100%" maxW="600px" alignSelf="center" align="stretch">
        <Text fontSize="md" fontWeight="600" color="gray.800">Tempo</Text>
        <Wrap spacing={2}>
          {TEMPOS.map((tempo) => (
            <WrapItem key={tempo.id}>
              <WizardChip
                label={tempo.label}
                selected={selectedTempo === tempo.id}
                onPress={() => setSelectedTempo(tempo.id)}
                data-testid={`wizard-tempo-${tempo.id}`}
              />
            </WrapItem>
          ))}
        </Wrap>
      </VStack>

      <Button
        size="lg"
        bg="brand.500"
        color="white"
        fontWeight="bold"
        w="100%"
        maxW="400px"
        alignSelf="center"
        mt={4}
        onClick={() => canSubmit && onSubmit({ genres: selectedGenres, moods: selectedMoods, tempo: selectedTempo })}
        isDisabled={!canSubmit || isLoading}
        data-testid="wizard-music-submit"
        _hover={{ bg: 'brand.600' }}
      >
        {isLoading ? 'Saving...' : 'Continue'}
      </Button>
    </VStack>
  );
}

export default MusicStep;
